import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import H from "history";
import Table, { TableColumn } from '../../Table';
import ApiService from '../../ApiService';
import DispatchProps from '../../../utils/DispatchProps';
import LoadingCard from '../../LoadingCard';
import AnalyticsDateControls from '../../AnalyticsDateControls';
import { IRootState } from '../../../store';
import { TableRowProps } from '../../TableRow';
import { StudentSubmissionSummary } from '../../../models/analytics/AssignmentSubmissionSummaries';
import { cellForColumn } from './SubmissionAssignmentAnalyticsUtils';
import AcademicYearPicker from '../../AcademicYearPicker';
import { openFileDownload } from '../../../utils/Utils';
import StudentSubmissionResponse from "../../../models/analytics/StudentSubmissionResponse";
import SchoolMenu from '../../SchoolMenu';
import PercentageOverviewDoughnut from "../../assignment-analytics/PercentageOverviewDoughnut";
import { CommandBarButton } from 'office-ui-fabric-react';
import SchoolPageTitle from '../../SchoolPageTitle';
import { TextField } from 'office-ui-fabric-react/lib-commonjs';
import { debounce } from 'throttle-debounce';
import AnalyticsControlBar from "../../assignment-analytics/AnalyticsControlBar";

interface SchoolStudentSubmissonAnalyticsPageProps {
    tenantId: string;
    schoolCode: string
    history: H.History;
    apiService: ApiService;
    startDate?: string,
    endDate?: string,
    academicYear: number,
}

interface SchoolStudentSubmissonAnalyticsPageState {
    result?: StudentSubmissionResponse;
    filteredResults?: StudentSubmissionSummary[];
    filter?: string;
}

class SchoolStudentSubmissonAnalyticsPage extends Component<SchoolStudentSubmissonAnalyticsPageProps & DispatchProps, SchoolStudentSubmissonAnalyticsPageState> {
    static displayName = SchoolStudentSubmissonAnalyticsPage.name;

    private readonly debouncedFilter: debounce<() => void>;

    constructor(props: SchoolStudentSubmissonAnalyticsPageProps & DispatchProps) {
        super(props);
        this.debouncedFilter = debounce(500, this.filterResults);
        this.state = { }
    }

    async componentDidMount() {
        if (this.props.startDate && this.props.endDate && this.props.academicYear) {
            await this.getResults();
        }
    }

    async componentDidUpdate(prevProps: Readonly<SchoolStudentSubmissonAnalyticsPageProps & DispatchProps>, prevState: any): Promise<void> {
        if (this.props.startDate !== prevProps.startDate || this.props.endDate !== prevProps.endDate || this.props.academicYear !== prevProps.academicYear) {
            await this.getResults();
        }

        if (this.state.result !== prevState.result) {
            this.filterResults();
        }

        if (this.state.filter !== prevState.filter) {
            this.debouncedFilter();
        }
    }

    componentWillUnmount() {
        if (this.debouncedFilter) {
            this.debouncedFilter.cancel();
        }
    }

    private async getResults(): Promise<void> {
        this.setState({
            result: undefined
        });

        const result = await this.props.apiService.getStudentSubmissionsAnalyticsForSchool(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!);

        this.setState({
            result
        });
    }

    private filterResults = () => {
        this.setState({
            filteredResults: this.state.result?.items.filter(this.filterResult)
        });
    }

    private filterResult = (item: StudentSubmissionSummary) => {
        if (this.state.filter) {
            return item.studentDisplayName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1 ||
                item.studentUserPrincipalName.toLowerCase().indexOf(this.state.filter.toLowerCase()) > -1;
        }
        else {
            return true;
        }
    }

    private onAcademicYearChanged = (academicYear: number) => {
        if (this.props.academicYear !== academicYear) {
            AnalyticsDateControls.SetDateRangeToAcademic(this.props.history, academicYear);
        }
    }

    private async export(): Promise<void> {
        var result = await this.props.apiService.getStudentSubmissionsAnalyticsForSchoolExport(this.props.tenantId, this.props.schoolCode, this.props.academicYear, this.props.startDate!, this.props.endDate!)
        openFileDownload(result);
    }

    public static readonly columns: TableColumn[] = [
        { title: "Student", propertyPath: "studentDisplayName", extraDetail: "User principal name", enableSort: true, flex: 4 },
        { title: "Year", propertyPath: "yearName", enableSort: true, flex: 1 },
        { title: "Assignments Received", propertyPath: "submissionsTotal", extraDetail: "Total assignments this student has received", enableSort: true, flex: 2 },
        { title: "Not Completed", propertyPath: "submissionsNotCompletedPercentage", extraDetail: "Not submitted within 2 weeks of due date", enableSort: true, flex: 2 },
        { title: "Late", propertyPath: "submissionsSubmittedAfterDueDatePercentage", extraDetail: "Submitted within 2 weeks after the due date", enableSort: true, flex: 2 },
        { title: "On Time", propertyPath: "submissionsSubmittedBeforeDueDatePercentage", extraDetail: "Submitted before the due date", enableSort: true, flex: 2 },
    ];

    private readonly rowProps = (item: StudentSubmissionSummary): TableRowProps => {
        return {
            enableRowLink: true,
            openInNewTab: true,
            to: `/manage/${this.props.tenantId}/${this.props.schoolCode}/student-assignment-search?userid=${item.studentId}&onlyCurrent=true`
        };
    }

    private buildResults(): JSX.Element {
        if (this.state.result === undefined || this.state.filteredResults === undefined) {
            return <LoadingCard />;
        }
        else {
            return <Fragment>
                <Table
                    title="Students"
                    items={this.state.filteredResults}
                    columns={SchoolStudentSubmissonAnalyticsPage.columns}
                    cellForColumn={cellForColumn}
                    rowPropsForItem={this.rowProps}
                    defaultSortColumnPath={'studentDisplayName'}
                    enableSort={true}>

                    <TextField className="filter-controls__input"
                        placeholder="Filter"
                        onChange={(event, newValue) => this.setState({ filter: newValue })} />

                    <CommandBarButton
                        iconProps={{ iconName: "DownloadDocument" }}
                        text="Export All"
                        onClick={() => this.export()} />
                </Table>
            </Fragment >
        }
    }

    private buildCharts = (): JSX.Element | undefined => {

        if (this.state.result === undefined) {
            return (<LoadingCard />);
        }

        return (
            <div className={"card flex-row flex--justify-content-space-around p-05rem"} style={{ marginTop: "0.25rem" }}>
                <div className={"number-stat"}>
                    <div className="number-stat__number number-stat__number--small">
                        {this.state.result.completedTotal}
                    </div>
                    <div className={"number-stat__description"}>Student Submissions</div>
                </div>
                <PercentageOverviewDoughnut percentageOverview={this.state.result.completedPercentageOverview} title={"% Students completing assignments"} labels={PercentageOverviewDoughnut.LABELS_ALWAYS} />
                <PercentageOverviewDoughnut percentageOverview={this.state.result.onTimePercentageOverview} title={"% Students completing assignments on time"} labels={PercentageOverviewDoughnut.LABELS_ALWAYS} />
            </div>
        );
    }

    render() {
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Assignment Analytics - Students" reverse={true} />
                <SchoolMenu prefix="Assignment Analytics" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/assignment-analytics-overview?academicYear=${this.props.academicYear}&startDate=${this.props.startDate}&endDate=${this.props.endDate}`} suffix="Students" />
                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <AnalyticsControlBar tenantId={this.props.tenantId} schoolCode={this.props.schoolCode}  current={"student"} startDate={this.props.startDate} endDate={this.props.endDate} academicYear={this.props.academicYear} onAcademicYearChanged={this.onAcademicYearChanged} history={this.props.history}/>
                 
                    {this.buildCharts()}
                    {this.buildResults()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): SchoolStudentSubmissonAnalyticsPageProps => {

        const { startDate, endDate } = AnalyticsDateControls.fromQueryStringOrDefault(ownProps.history.location.search);
        const academicYear = AcademicYearPicker.fromQueryStringOrDefault(ownProps.history.location.search);

        return {
            apiService: state.auth.apiService,
            history: ownProps.history,
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            startDate: startDate,
            endDate: endDate,
            academicYear: academicYear
        };
    }
)(SchoolStudentSubmissonAnalyticsPage)
